import axiosInstance from "@/api/axiosInstance";
import { ENDPOINTS } from "@/constants/api";
import { requestHandler } from "@/helpers/requestHandler";

export interface WindAlertEvaluation {
  id: number;
  userId: number;
  userEmail: string | null;
  spotId: number;
  spotName: string | null;
  modelKey: string;
  alertDate: string;
  alertType: "early" | "reminder" | "morning_of";
  peakKnots: number;
  peakGustKnots: number | null;
  peakWindDirection: number | null;
  dayName: string | null;
  minKnotsThreshold: number;
  confidence: string | null;
  delivered: boolean;
  deliveredAt: string | null;
  deliveryChannel: string | null;
  verified: boolean | null;
  actualPeakKnots: number | null;
  createdAt: string | null;
  evaluatedAt: string;
}

export interface DigestEntry {
  spotId: number;
  modelKey: string;
  knots?: number;
  gust?: number;
  peakTimeLocal?: string;
}

export interface TriggerWindAlertRequest {
  userId: number;
  spotId: number;
  modelKey: string;
  /**
   * Naive local-time string "YYYY-MM-DDTHH:mm" interpreted by the server in the
   * SPOT's timezone. Preferred over `timestamp` — no ambiguity about which tz the
   * admin was thinking in. Backend rejects the request if both are missing.
   */
  peakTimeLocal?: string;
  /** Absolute peak hour in epoch milliseconds. Back-compat; `peakTimeLocal` wins if both set. */
  timestamp?: number;
  knots?: number;
  gust?: number;
  reminder?: boolean;
}


export interface TriggerWindAlertResponse {
  delivered: boolean;
  userId: number;
  userEmail: string | null;
  spotId: number;
  spotName: string;
  lat: number;
  lng: number;
  modelKey: string;
  timestamp: number;
  knots: number;
  gust: number;
  reminder: boolean;
}

export const WindAlertAdminService = {
  async trigger(req: TriggerWindAlertRequest): Promise<TriggerWindAlertResponse> {
    return requestHandler<TriggerWindAlertResponse>(async () => {
      const response = await axiosInstance.post(
        ENDPOINTS.WIND_ALERTS.ADMIN_TRIGGER,
        req
      );
      return response.data as TriggerWindAlertResponse;
    }, "WindAlertAdminService -> trigger");
  },

  async triggerDigest(userId: number, entries: DigestEntry[]): Promise<{ sent: boolean; email: string; entries: number }> {
    return requestHandler(async () => {
      const response = await axiosInstance.post(
        ENDPOINTS.WIND_ALERTS.ADMIN_TRIGGER_DIGEST,
        { userId, entries }
      );
      return response.data;
    }, "WindAlertAdminService -> triggerDigest");
  },

  async fetchEvaluationsPaginated(params: {
    limit?: number; offset?: number; userId?: number; spotId?: number; status?: string;
    sortBy?: string; sortDir?: string;
  }): Promise<{ rows: WindAlertEvaluation[]; total: number }> {
    return requestHandler(async () => {
      const response = await axiosInstance.get(ENDPOINTS.WIND_ALERTS.ADMIN_EVALUATIONS, { params });
      return response.data as { rows: WindAlertEvaluation[]; total: number };
    }, "WindAlertAdminService -> fetchEvaluationsPaginated");
  },
};
